import { yupResolver } from '@hookform/resolvers/yup';
import Check from '@mui/icons-material/Check';
import Close from '@mui/icons-material/Close';
import Edit from '@mui/icons-material/Edit';
import { useTheme } from '@mui/material';
import Box from '@mui/material/Box';
import Button, { buttonClasses } from '@mui/material/Button';
import FormControlLabel from '@mui/material/FormControlLabel';
import Switch from '@mui/material/Switch';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import Typography from '@mui/material/Typography';
import {
  patchV1CompaniesCompanyIdJobsId,
  patchV1CompaniesCompanyIdJobsJobIdSummary,
} from '@treadinc/horizon-api-spec';
import { t } from 'i18next';
import { snakeCase } from 'lodash-es';
import { observer } from 'mobx-react-lite';
import { ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';

import JobSummaryCard from '~components/Job/JobSummaryCard';
import { JobSummaryEditFormHandler } from '~components/Job/JobSummaryEditForm';
import JobTimeline from '~components/Job/JobTimeline';
import Loads from '~components/Job/Loads';
import { RouteMap } from '~components/Job/RouteMap';
import { NEW_DISPATCH_TOPBAR_CONTROLS_HEIGHT_IN_PX } from '~constants/filters';
import useAddOns from '~hooks/useAddOns/useAddOns';
import { trackApprovalBatch } from '~hooks/useAnalytics/trackApprovals';
import useDispatchNumberLabel from '~hooks/useDispatchNumberLabel';
import type { DriverDay } from '~hooks/useDriverDays/models';
import { useDriverDays } from '~hooks/useDriverDays/useDriverDays';
import { DriverBasic } from '~hooks/useDrivers/models';
import { Job, JobSummary } from '~hooks/useJob/models';
import { useJob } from '~hooks/useJob/useJob';
import { useOrders } from '~hooks/useOrders/useOrders';
import useResourceUsageLogs from '~hooks/useResourceUsageLogs/useResourceUsageLogs';
import useApprovalCardShadow from '~pages/Approvals/shared/useApprovalCardShadow';
import connection from '~services/connectionModule';
import reportRequestError from '~services/reportRequestError';
import { useStores } from '~store';
import { alert, AlertTypes } from '~types/AlertTypes';
import { getApprovalStatus } from '~utils/jobs/job-utils';
import { getEffectiveUserCompanyId } from '~utils/user/user-utils';

import {
  AddOnChargeDTO,
  JobDetailsFormData,
  ResourceUsageLogDTO,
  schemaHelpers,
} from '../../DriverPay/helpers';
import ApprovalActionsMenu from './ApprovalActionsMenu';
import ChargesTable from './ChargesTable';

const emDash = '-';

const getFromToLocationString = (from: string, to: string) => `${from} to: ${to}`;

interface JobHeaderProps {
  hideMapToggle?: boolean;
  job: Job;
  onToggleJob: (job: Job) => void;
  toggledJob: Job | null;
}

export const JobHeader = ({
  job,
  hideMapToggle = false,
  toggledJob,
  onToggleJob,
}: JobHeaderProps) => {
  const theme = useTheme();
  const { singular } = useDispatchNumberLabel();
  const fragments = useMemo(() => {
    const fragments: string[] = [];

    if (job.project?.externalId) {
      fragments.push(`${t('approvals.project_id')}: ${job.project.externalId}`);
    }

    if (job.order?.dispatchNumber?.name) {
      fragments.push(
        `${singular('approvals.dispatch_number')}: ${job.order.dispatchNumber.name}`,
      );
    }

    fragments.push(
      getFromToLocationString(
        job.pickUpWayPoints[0]?.site?.name ?? emDash,
        job.dropOffWayPoints[0]?.site?.name ?? emDash,
      ),
    );

    const rate = job.haulerRateType || job.driverRateType || job.driverRate?.type;

    if (rate) {
      fragments.push(`${t('common.rate')}: ${t(`rate_types.${snakeCase(rate)}`)}`);
    }

    return fragments;
  }, [job, singular]);

  return (
    <Box
      sx={{
        display: 'flex',
        gap: 1,
        alignItems: 'center',
        bgcolor: theme.brandV2.colors.treadGray10,
        py: 1,
        px: 1.5,
        borderRadius: 2,
        borderBottomRightRadius: 0,
        borderBottomLeftRadius: 0,
      }}
    >
      <Typography variant="subtitle1">{fragments.join(' • ')}</Typography>

      {!hideMapToggle && (
        <FormControlLabel
          control={
            <Switch
              size="small"
              checked={toggledJob?.id === job.id}
              onChange={() => {
                onToggleJob(job);
              }}
            />
          }
          label={t('approvals.driver_day.show_map_toggle')}
          sx={{ ml: 'auto' }}
        />
      )}
    </Box>
  );
};

export const JobDetailsTabs = ({
  hideRouteHistoryAction,
  mapActions,
  showTimeline,
  toggledJob,
}: {
  hideRouteHistoryAction?: boolean;
  mapActions?: ReactNode;
  showTimeline?: boolean;
  toggledJob: Job;
}) => {
  const theme = useTheme();
  const [selectedTabIndex, setSelectedTabIndex] = useState(0);
  const cardShadow = useApprovalCardShadow();

  return (
    <Box
      sx={{
        backgroundColor: theme.semantic.surfaceRaised,
        border: `1px solid ${theme.brandV2.colors.treadGray7}`,
        borderRadius: 2,
        boxShadow: cardShadow,
        pb: 1.5,
      }}
    >
      <Tabs
        onChange={(_event, value) => setSelectedTabIndex(value)}
        value={selectedTabIndex}
        slotProps={{ indicator: { style: { display: 'none' } } }}
      >
        <Tab
          label="Map"
          sx={{
            color: theme.brandV2.colors.treadBlack,
            fontSize: '12px',
            mb: 1,
            ml: 1,
            pb: 1,
            width: 'fit-content',
            '&.Mui-selected': {
              borderBottom: `4px solid ${theme.brandV2.colors.treadYellow}`,
            },
          }}
        />

        {showTimeline && (
          <Tab
            label="Timeline"
            sx={{
              color: theme.brandV2.colors.treadBlack,
              fontSize: '12px',
              mb: 1,
              ml: 1,
              pb: 1,
              width: 'fit-content',
              '&.Mui-selected': {
                borderBottom: `4px solid ${theme.brandV2.colors.treadYellow}`,
              },
            }}
          />
        )}
      </Tabs>

      <Box role="tabpanel" hidden={selectedTabIndex !== 0}>
        <RouteMap
          job={toggledJob}
          extraActions={mapActions}
          hideRouteHistoryAction={hideRouteHistoryAction}
        />
      </Box>

      <Box role="tabpanel" hidden={selectedTabIndex !== 1}>
        <JobTimeline
          job={toggledJob}
          sx={{ height: '348px', overflow: 'auto', px: 1.5 }}
        />
      </Box>
    </Box>
  );
};

type formData = {
  addOnCharges: AddOnChargeDTO[];
  resourceUsageLogs: ResourceUsageLogDTO[];
};

interface JobDetailsProps {
  driverDay?: DriverDay;
  editable: boolean;
  hideApprovalCTA?: boolean;
  hideJobSummaryCard?: boolean;
  hideMapToggle?: boolean;
  job: Job;
  onJobUpdated?: (job: Job) => void;
  onReloadJob?: () => void;
  onToggleJob: (job: Job) => void;
  toggledJob: Job | null;
  forceExitEditMode?: boolean;
}

const JobDetails = ({
  driverDay,
  editable,
  hideApprovalCTA,
  hideJobSummaryCard = false,
  hideMapToggle,
  job,
  onJobUpdated,
  onReloadJob,
  onToggleJob,
  toggledJob,
  forceExitEditMode,
}: JobDetailsProps) => {
  const theme = useTheme();
  const cardShadow = useApprovalCardShadow();
  const { addOnsStore, toasterStore, ordersStore, userStore, driverDayStore } =
    useStores();
  const { approveJob, unapproveJob } = useJob();
  const { batchUpdateAddOnChargesByJob, getAllAddOnChargesByJob } = useAddOns();
  const { getOrderPhasesTypeahead } = useOrders();
  const { fetchJobsForDriverDay } = useDriverDays();
  const [isEditing, setIsEditing] = useState(false);
  const editSessionIdRef = useRef(0);
  const editedInternalNotesRef = useRef<string | null>(null);
  const [internalNotesDirty, setInternalNotesDirty] = useState(false);
  const { getResourceUsageLogsByJob, batchUpdateResourceUsageLogsByJob } =
    useResourceUsageLogs();
  const userPermissions = userStore.getPermissions();
  const driverDayId = driverDay?.id;
  const companyId = getEffectiveUserCompanyId(userStore);

  const addOns = useMemo(() => {
    return addOnsStore.addOnChargesByJobId[job.id] ?? [];
  }, [job.id, addOnsStore.addOnChargesByJobId]);

  const resourceUsageLogs = useMemo(() => {
    return addOnsStore.resourceUsageLogsByJobId[job.id] ?? [];
  }, [job.id, addOnsStore.resourceUsageLogsByJobId]);

  const jobHasCharges = useMemo(
    () => addOns.length + resourceUsageLogs.length > 0,
    [addOns, resourceUsageLogs],
  );
  const phasesOptions = useMemo(
    () => ordersStore.phaseTypeheadsByOrderID[job.order?.id ?? ''] ?? [],
    [ordersStore.phaseTypeheadsByOrderID, job.order?.id],
  );

  useEffect(() => {
    getAllAddOnChargesByJob(companyId, job.id);
    getResourceUsageLogsByJob(companyId, job.id);
    getOrderPhasesTypeahead({
      companyId,
      orderId: job.order?.id ?? '',
    });
  }, [job.id, companyId]);

  useEffect(() => {
    const rtuTimestamp = driverDayStore.rtuJobUpdates[job.id];
    if (rtuTimestamp && !isEditing) {
      getResourceUsageLogsByJob(companyId, job.id);
    }
  }, [driverDayStore.rtuJobUpdates[job.id], job.id, isEditing]);

  useEffect(() => {
    if (forceExitEditMode && isEditing) {
      editSessionIdRef.current += 1;
      setIsEditing(false);
      resetForm();
    }
  }, [forceExitEditMode, isEditing]);

  const formMethods = useForm({
    resolver: yupResolver(JobDetailsFormData),
    defaultValues: {
      addOnCharges: addOns.map((addOnCharge) => {
        return schemaHelpers.addOnChargeToAddOnChargeDTO(addOnCharge);
      }),
      resourceUsageLogs: resourceUsageLogs.map((resourceUsageLog) => {
        return schemaHelpers.resourceUsageLogToResourceUsageLogDTO(resourceUsageLog);
      }),
    },
    mode: 'all',
    delayError: 500,
  });

  useEffect(() => {
    // Only update form values when not editing to avoid overwriting user changes
    if (!isEditing) {
      setValue(
        'addOnCharges',
        addOns.map((addOnCharge) => {
          return schemaHelpers.addOnChargeToAddOnChargeDTO(addOnCharge);
        }),
      );
    }
  }, [addOns, isEditing]);

  useEffect(() => {
    // Only update form values when not editing to avoid overwriting user changes
    if (!isEditing) {
      setValue(
        'resourceUsageLogs',
        resourceUsageLogs.map((resourceUsageLog) => {
          return schemaHelpers.resourceUsageLogToResourceUsageLogDTO(resourceUsageLog);
        }),
      );
    }
  }, [resourceUsageLogs, isEditing]);

  const canEditJobSummary = Boolean(job.jobSummary?.editable);
  const editJobSummaryFormRef = useRef<JobSummaryEditFormHandler>(null);
  const [isEditingJobSummary, setIsEditingJobSummary] = useState(false);
  const [isSubmittingEditJobSummaryForm, setIsSubmittingEditJobSummaryForm] =
    useState(false);
  const [isApprovalLoading, setIsApprovalLoading] = useState(false);
  const {
    isApproved: jobApproved,
    isUnapprovable: jobUnapprovable,
    isApprovable: jobApprovable,
  } = getApprovalStatus(job, userPermissions, Boolean(job.order?.editable));

  const handleApprove = useCallback(async () => {
    setIsApprovalLoading(true);
    try {
      const updatedJob = await approveJob(job.id, companyId);

      if (updatedJob) {
        trackApprovalBatch('job', 1);
      }

      if (onJobUpdated) {
        if (updatedJob) {
          onJobUpdated(updatedJob);
        }
      } else if (driverDayId) {
        fetchJobsForDriverDay(driverDayId, true);
      }
    } catch (error) {
      connection.handleRequestError(
        error,
        t('error_messages.jobs.failed_to_approve_job'),
      );
    } finally {
      setIsApprovalLoading(false);
    }
  }, [job.id, companyId, onJobUpdated, driverDayId, fetchJobsForDriverDay]);

  const handleUnapprove = useCallback(async () => {
    setIsApprovalLoading(true);
    try {
      if (onJobUpdated) {
        const updatedJob = await unapproveJob(job.id, companyId);
        if (updatedJob) {
          onJobUpdated(updatedJob);
        }
      } else {
        await unapproveJob(job.id, companyId);
        if (driverDayId) {
          fetchJobsForDriverDay(driverDayId, true);
        }
      }
    } catch (error) {
      connection.handleRequestError(
        error,
        t('error_messages.jobs.failed_to_unapprove_job'),
      );
    } finally {
      setIsApprovalLoading(false);
    }
  }, [job.id, companyId, onJobUpdated, driverDayId, fetchJobsForDriverDay]);

  const handleSubmitEditJobSummaryForm = useCallback(
    async (job: Job) => {
      const formData = await editJobSummaryFormRef.current?.onSubmit();

      if (!formData) {
        return;
      }

      setIsSubmittingEditJobSummaryForm(true);

      try {
        const response = await patchV1CompaniesCompanyIdJobsJobIdSummary({
          path: { 'company-id': companyId, 'job-id': job.id },
          body: {
            adjusted_logging_started_at: formData.loggingStartedAt?.toISOString() ?? null,
            adjusted_logging_ended_at: formData.loggingEndedAt?.toISOString() ?? null,
            approved_work_time_minutes: formData.approvedWorkTimeMinutes,
          },
        });
        job.jobSummary = JobSummary.parse(response.data.data);
        onJobUpdated?.(job);

        toasterStore.push(
          alert(t('approvals.approve_jobs.job_summary_updated'), AlertTypes.success),
        );
      } catch (error) {
        connection.handleRequestError(
          error,
          t('error_messages.jobs.failed_to_update_job_summary'),
        );
      } finally {
        setIsEditingJobSummary(false);
        setIsSubmittingEditJobSummaryForm(false);
      }
    },
    [userStore, onJobUpdated, toasterStore],
  );

  const {
    handleSubmit,
    reset,
    setValue,
    formState: { isValid, isDirty },
  } = formMethods;

  // State cannot block re-entry: two submits both read it before React commits the update.
  const isSavingRef = useRef(false);
  const [isSaving, setIsSaving] = useState(false);

  const onSubmit = async (data: formData) => {
    if (isSavingRef.current) {
      return;
    }

    isSavingRef.current = true;
    setIsSaving(true);

    const submittedEditSessionId = editSessionIdRef.current;
    const promises: Promise<unknown>[] = [];

    if (isDirty) {
      promises.push(
        batchUpdateResourceUsageLogsByJob(
          companyId,
          job.id,
          data.resourceUsageLogs,
          job.jobStartAt.toISOString(),
        ),
        batchUpdateAddOnChargesByJob(companyId, job.id, data.addOnCharges),
      );
    }

    if (internalNotesDirty) {
      promises.push(
        patchV1CompaniesCompanyIdJobsId({
          path: { 'company-id': companyId, id: job.id },
          body: {
            internal_notes: editedInternalNotesRef.current || null,
          },
        }).then((response) => {
          const updatedJob = Job.parse(response.data.data);
          onJobUpdated?.(updatedJob);
        }),
      );
    }

    try {
      await Promise.all(promises);

      // A forced exit or Cancel mid-save abandons this session; leave its successor alone.
      if (editSessionIdRef.current === submittedEditSessionId) {
        setIsEditing(false);
        editedInternalNotesRef.current = null;
        setInternalNotesDirty(false);
        reset();
      }

      toasterStore.push(alert(t('success_messages.changes_saved'), AlertTypes.success));
    } catch (error) {
      reportRequestError(error, t('error_messages.jobs.failed_to_update'));
    } finally {
      isSavingRef.current = false;
      setIsSaving(false);
    }
  };

  const resetForm = () => {
    editedInternalNotesRef.current = null;
    setInternalNotesDirty(false);
    reset({
      addOnCharges: addOns.map((addOnCharge) => {
        return schemaHelpers.addOnChargeToAddOnChargeDTO(addOnCharge);
      }),
      resourceUsageLogs: resourceUsageLogs.map((resourceUsageLog) => {
        return schemaHelpers.resourceUsageLogToResourceUsageLogDTO(resourceUsageLog);
      }),
    });
  };

  const ApprovalCTA = observer(() => {
    if (hideApprovalCTA) {
      return null;
    }

    const isJobWaitingForRTU = driverDayStore.isJobWaitingForRTU(job.id);

    return jobApproved ? (
      <ApprovalActionsMenu
        onMenuItemClick={jobUnapprovable ? handleUnapprove : undefined}
        approved={jobApproved}
        isLoading={
          isApprovalLoading ||
          isJobWaitingForRTU ||
          (driverDayId ? driverDayStore.isShiftApprovalLoading(driverDayId) : false)
        }
      />
    ) : jobApprovable ? (
      <Button
        color="brandV2Yellow"
        loading={isApprovalLoading}
        sx={{
          '&.MuiButtonBase-root': {
            height: `${NEW_DISPATCH_TOPBAR_CONTROLS_HEIGHT_IN_PX}px`,
          },
        }}
        onClick={handleApprove}
        disabled={
          isEditing ||
          isJobWaitingForRTU ||
          (driverDayId ? driverDayStore.isShiftApprovalLoading(driverDayId) : false)
        }
      >
        {t('approvals.driver_day.approve_job')}
      </Button>
    ) : null;
  });

  const EditingButton = () => {
    if (!editable) {
      return null;
    }

    const isJobWaitingForRTU = driverDayStore.isJobWaitingForRTU(job.id);

    return isEditing ? (
      <Box
        sx={{
          display: 'flex',
          gap: 1.5,
        }}
      >
        <Button
          onClick={() => {
            if (isSavingRef.current) {
              return;
            }

            editSessionIdRef.current += 1;
            setIsEditing(false);
            resetForm();
          }}
          variant="outlined"
          color="secondary"
          sx={{
            fontWeight: 600,
            '&.MuiButton-root': {
              borderColor: theme.brandV2.colors.treadGray7,
              height: `${NEW_DISPATCH_TOPBAR_CONTROLS_HEIGHT_IN_PX}px`,
            },
          }}
          startIcon={<Close />}
          disabled={isSaving}
        >
          {t('actions.cancel')}
        </Button>

        <Button
          type="submit"
          variant={'contained'}
          color={'brandV2Green'}
          sx={{
            fontWeight: 600,
            '&.MuiButton-root': {
              borderColor: theme.brandV2.colors.treadGray7,
              height: `${NEW_DISPATCH_TOPBAR_CONTROLS_HEIGHT_IN_PX}px`,
            },
          }}
          startIcon={<Check />}
          loading={isSaving}
          disabled={!isValid || (!isDirty && !internalNotesDirty)}
        >
          {t('actions.save_changes')}
        </Button>
      </Box>
    ) : (
      <Button
        variant="outlined"
        color="secondary"
        onClick={() => {
          setIsEditing(!isEditing);
        }}
        sx={{
          fontWeight: 600,
          '&.MuiButton-root': {
            borderColor: theme.brandV2.colors.treadGray7,
            height: `${NEW_DISPATCH_TOPBAR_CONTROLS_HEIGHT_IN_PX}px`,
          },
        }}
        startIcon={<Edit />}
        disabled={
          isApprovalLoading ||
          isJobWaitingForRTU ||
          (driverDayId ? driverDayStore.isShiftApprovalLoading(driverDayId) : false)
        }
      >
        {t('actions.edit')}
      </Button>
    );
  };

  const ActionsSection = () => {
    if (isEditing) return <EditingButton />;
    else {
      return (
        <Box
          sx={{
            display: 'flex',
            gap: 1.5,
          }}
        >
          <EditingButton />

          <ApprovalCTA />
        </Box>
      );
    }
  };

  return (
    <Box
      key={job.id}
      id="job-details-form"
      aria-label="job details"
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 1.5,
        backgroundColor: theme.semantic.surfaceRaised,
        border: `1px solid ${theme.brandV2.colors.treadGray7}`,
        borderRadius: 2,
        boxShadow: cardShadow,
        pb: 1,
      }}
    >
      <JobHeader
        hideMapToggle={hideMapToggle}
        job={job}
        onToggleJob={onToggleJob}
        toggledJob={toggledJob}
      />

      {!hideJobSummaryCard && (
        <Box
          sx={{
            alignItems: 'flex-start',
            display: 'flex',
            gap: 2,
            justifyContent: 'space-between',
            mb: 1.5,
            p: 1.5,
          }}
        >
          <JobSummaryCard
            ref={editJobSummaryFormRef}
            isEditing={isEditingJobSummary}
            isSubmittingForm={isSubmittingEditJobSummaryForm}
            job={job}
            sx={{ flex: 1, p: 0 }}
          />

          <Box sx={{ display: 'flex', gap: 1.5 }}>
            {canEditJobSummary && (
              <EditJobSummaryCTA
                isEditing={isEditingJobSummary}
                onClick={() => setIsEditingJobSummary((state) => !state)}
              />
            )}

            {isEditingJobSummary && (
              <SubmitJobSummaryFormCTA
                isSubmitting={isSubmittingEditJobSummaryForm}
                onClick={() => handleSubmitEditJobSummaryForm(job)}
              />
            )}
          </Box>
        </Box>
      )}

      <FormProvider {...formMethods}>
        <Box component="form" onSubmit={handleSubmit(onSubmit)}>
          <Box>
            <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: jobHasCharges ? 'space-between' : 'flex-end',
                  px: 1.5,
                }}
              >
                {jobHasCharges ? (
                  <Typography
                    variant="subtitle1"
                    sx={{
                      fontWeight: 600,
                    }}
                  >
                    {t('approvals.phase_code_hours')}
                  </Typography>
                ) : null}
                <ActionsSection />
              </Box>
            </Box>

            <ChargesTable
              isEditing={isEditing}
              resourceUsageLogs={resourceUsageLogs}
              addOns={addOns}
              phasesOptions={phasesOptions}
              driver={job.driver ?? (driverDay?.driver as DriverBasic)}
              isJobLevel
              owningCompanyId={companyId}
              orderId={job.order.id ?? undefined}
              projectId={job.project?.id}
              reloadOrderPhases={() =>
                getOrderPhasesTypeahead({
                  companyId,
                  orderId: job.order?.id ?? '',
                })
              }
            />
          </Box>

          <Box sx={{ px: 1.5 }}>
            <Loads
              key={job.id}
              details={job}
              readOnly={false}
              reload={async () => {
                if (onReloadJob) {
                  onReloadJob();
                } else if (driverDayId) {
                  await fetchJobsForDriverDay(driverDayId, true);
                }
              }}
              isEditing={isEditing}
              loadsTableSx={{ border: 0, mx: -1.5 }}
              hideJobSummaryCard
              disableApproval={
                isApprovalLoading ||
                driverDayStore.isJobWaitingForRTU(job.id) ||
                (driverDayId ? driverDayStore.isShiftApprovalLoading(driverDayId) : false)
              }
              onInternalNotesChange={(notes) => {
                editedInternalNotesRef.current = notes;
                setInternalNotesDirty(notes !== (job.internalNotes ?? ''));
              }}
            />
          </Box>
        </Box>
      </FormProvider>
    </Box>
  );
};

interface EditJobSummaryCTAProps {
  isEditing: boolean;
  onClick: () => void;
}

function EditJobSummaryCTA({ isEditing, onClick }: EditJobSummaryCTAProps) {
  const theme = useTheme();

  return (
    <Button
      color="secondary"
      onClick={onClick}
      startIcon={isEditing ? <Close /> : <Edit />}
      variant="outlined"
      sx={{
        fontWeight: 600,
        [`&.${buttonClasses.root}`]: { borderColor: theme.brandV2.colors.treadGray7 },
      }}
    >
      {isEditing ? t('actions.cancel') : t('actions.edit')}
    </Button>
  );
}

interface SubmitJobSummaryFormCTAProps {
  isSubmitting: boolean;
  onClick: () => void;
}

function SubmitJobSummaryFormCTA({
  isSubmitting,
  onClick,
}: SubmitJobSummaryFormCTAProps) {
  return (
    <Button
      color="success"
      loading={isSubmitting}
      onClick={onClick}
      size="small"
      startIcon={<Check />}
    >
      {t('actions.save')}
    </Button>
  );
}

export default observer(JobDetails);
